import moment from 'moment';
import { groupBy, sortBy } from 'utils';
export function getGroupedPlaces(hotelList) {
    const grouped = groupBy(hotelList, (hotel) => hotel.destination.id);
    const result = Object.entries(grouped)
        .map((item) => ({
        destination: item[1].find((h) => h.destination.id === parseInt(item[0])).destination,
        hotels: item[1].sort(sortBy('name')),
        nonSelectable: item[1].every((h) => h.isExternal)
    }))
        .sort(sortBy((item) => item.destination.name));
    return result;
}
export function getGroupedChains(hotelList) {
    const grouped = groupBy(hotelList, (hotel) => { var _a; return ((_a = hotel.chain) === null || _a === void 0 ? void 0 : _a.id) || 0; });
    const result = Object.entries(grouped)
        .map((item) => ({
        chain: parseInt(item[0]) > 0 ? item[1].find((h) => { var _a; return ((_a = h.chain) === null || _a === void 0 ? void 0 : _a.id) === parseInt(item[0]); }).chain : null,
        items: getGroupedPlaces(item[1].sort(sortBy('name')))
    }))
        .sort(sortBy((item) => !item.chain));
    return result;
}
function getMonthsBetweenDates(strDate, eDate, lastProcessedDate) {
    const startDate = moment(strDate);
    const endDate = moment(eDate);
    const lastDay = lastProcessedDate ? moment(lastProcessedDate) : moment().add(6, 'months');
    let months = [];
    while (endDate > startDate || startDate.format('M') === endDate.format('M')) {
        if (startDate.startOf('day').isBefore(lastDay) || startDate.isSame(lastDay)) {
            months.push(startDate.format('YYYY-MM'));
        }
        startDate.add(1, 'month');
    }
    return months;
}
export function getDatesToQuote(dates, hotelBookerRates) {
    const format = 'YYYY-MM-DD';
    let sDay = moment().startOf('month');
    let lDay = moment().startOf('month').add(1, 'months');
    if (dates && dates.startDate && dates.endDate) {
        sDay = moment(dates.startDate).startOf('month');
        lDay = moment(dates.endDate).startOf('month');
    }
    if (sDay.isSame(lDay, 'month') && sDay.isSame(lDay, 'year')) {
        lDay = moment(sDay).startOf('month').add(1, 'month'); // Ensure we have at least one month range
    }
    const months = getMonthsBetweenDates(sDay, lDay, hotelBookerRates.lastProcessedDate);
    const monthsToQuote = months.filter(value => !hotelBookerRates.months.includes(value));
    const datesToQuote = [];
    monthsToQuote.forEach(month => {
        const d = month.split('/');
        const date = moment(`${d[1]}-${d[0]}-01`, format);
        if (moment().startOf('month').isBefore(date) || moment().startOf('month').isSame(date)) {
            datesToQuote.push(date);
        }
    });
    if (datesToQuote.length > 0) {
        const initialDate = moment(moment.min(datesToQuote).format(format), format);
        const finalDate = moment(moment.max(datesToQuote).endOf('month').format(format), format);
        return {
            startDate: initialDate.format(format),
            endDate: finalDate.format(format),
            months: monthsToQuote
        };
    }
    return null;
}
export function getTotalAdults(rooms) {
    let adults = rooms.reduce((totalAdults, room) => {
        return (totalAdults += room.adults ? room.adults : 0);
    }, 0);
    return adults > 0 ? adults : 0;
}
export function getTotalChildren(rooms) {
    let childrens = rooms.reduce((totalChildren, room) => {
        return (totalChildren += room.children ? room.children : 0);
    }, 0);
    return childrens > 0 ? childrens : 0;
}
export function getDetailedServiceForm(serviceForm, addOn, onSubmit) {
    let newData = Object.assign({}, serviceForm);
    switch (addOn.numberPersonsStandaloneType) {
        case 'NOT_RESTRICTED':
            newData.maxAdults = -1;
            newData.maxChildren = -1;
            break;
        case 'RESTRICTED':
            if ((addOn.maxAdults && addOn.maxAdults > 0) || (addOn.maxChildren && addOn.maxChildren > 0)) {
                newData.maxAdults = addOn.maxAdults;
                newData.maxChildren = addOn.maxChildren;
            }
            break;
    }
    switch (addOn.chargeType) {
        case 'PERSON_PRICE':
            newData.maxChildren = 0;
            break;
    }
    if (addOn.hasQuantity) {
        newData.hasQuantity = true;
        newData.maxQuantity = addOn.maxQuantity;
        newData.minQuantity = addOn.minQuantity;
        newData.quantity = addOn.minQuantity;
    }
    newData.hasNumberPersons = addOn.hasNumberPersons;
    newData.allotmentDates = addOn.allotmentDates;
    newData.isRangeDate = addOn.dateType === 'DATE_RANGE';
    return Object.assign(Object.assign({}, newData), { onSubmit: onSubmit(addOn) });
}
export function getExtraParamsFromQuery(query, others, utmOnly) {
    if (!query) {
        return null;
    }
    const utm_keys = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "utm_extra",
        'highlightedRatePlanId'
    ];
    const keys = utmOnly ? utm_keys : [
        "_ga",
        "_gl",
        "_ga_session_id",
        "_ga_session_number",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "utm_extra",
        "gclid",
        "dclid",
        "_gac",
        "_gi",
        "_gp",
        "_gid",
        "fbclid",
        "no_redirect",
        "channelId"
    ];
    let result = keys.reduce((acc, key) => {
        if (query[key]) {
            acc[key] = query[key];
        }
        return acc;
    }, {});
    if (others) {
        Object.entries(others).forEach(([key, value]) => {
            if (value) {
                if (!result) {
                    result = {};
                }
                result[key] = value;
            }
        });
    }
    return result;
}
/**
 * Obtiene fechas válidas de check-in y check-out basadas en las restricciones de la promoción
 * @param promotion - La promoción con travelWindowPeriods y weekDaySettings
 * @returns Objeto con startDate (check-in) y endDate (check-out) en formato YYYY-MM-DD, o null si no hay fechas válidas
 */
export function getValidPromotionDates(promotion) {
    const { travelWindowPeriods, weekDaySettings } = promotion;
    if (!travelWindowPeriods || travelWindowPeriods.length === 0) {
        return null;
    }
    // Función para parsear fecha YYYY-MM-DD a Date
    const parseDateYYYYMMDD = (dateStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    };
    // Función para formatear Date a YYYY-MM-DD
    const formatDateYYYYMMDD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    // Función para obtener el día de la semana ISO (1=Lunes, 7=Domingo)
    const getISOWeekday = (date) => {
        const day = date.getDay();
        return day === 0 ? 7 : day; // Convertir domingo de 0 a 7
    };
    // Función para agregar días a una fecha
    const addDays = (date, days) => {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    };
    // Función para validar si un día de la semana está permitido
    const isDayAllowed = (date) => {
        if (!weekDaySettings)
            return true;
        const dayOfWeek = getISOWeekday(date);
        return weekDaySettings[dayOfWeek] === true;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Ordenar los períodos por fecha de inicio
    const sortedPeriods = [...travelWindowPeriods].sort((a, b) => {
        return parseDateYYYYMMDD(a.startDate).getTime() - parseDateYYYYMMDD(b.startDate).getTime();
    });
    // Buscar dentro de cada período ordenado
    for (const period of sortedPeriods) {
        const periodStart = parseDateYYYYMMDD(period.startDate);
        const periodEnd = parseDateYYYYMMDD(period.endDate);
        // Si el período termina antes de hoy, saltar al siguiente
        if (periodEnd < today)
            continue;
        // Empezar desde hoy o desde el inicio del período (lo que sea mayor)
        let checkIn = periodStart > today ? new Date(periodStart) : new Date(today);
        // Iterar dentro del período actual
        while (checkIn <= periodEnd) {
            const checkOut = addDays(checkIn, 2);
            // Verificar si el check-in cumple con weekDaySettings y ambas fechas están en el período
            const checkInAllowed = isDayAllowed(checkIn);
            const checkOutInPeriod = checkOut <= periodEnd;
            if (checkInAllowed && checkOutInPeriod) {
                return {
                    startDate: formatDateYYYYMMDD(checkIn),
                    endDate: formatDateYYYYMMDD(checkOut),
                };
            }
            // Avanzar al siguiente día
            checkIn = addDays(checkIn, 1);
        }
    }
    return null;
}
export function parseUtmToRecords(utm) {
    if (!utm)
        return null;
    const result = {};
    if (utm.source)
        result.utm_source = utm.source;
    if (utm.medium)
        result.utm_medium = utm.medium;
    if (utm.campaign)
        result.utm_campaign = utm.campaign;
    return Object.keys(result).length > 0 ? result : null;
}
export function getSecondsByUnit(unit) {
    switch (unit) {
        case 'MINUTE':
            return 60;
        case 'HOUR':
            return 3600;
        case 'DAY':
            return 86400;
        case 'WEEK':
            return 604800;
        case 'MONTH':
            return 2592000;
    }
}
